// src/components/contexts/EditContextModal.js

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Grid,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info'; // Import InfoIcon for tooltips
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import PropTypes from 'prop-types'; // Import PropTypes

// Define Type options
const TYPE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'app', label: 'App' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'server', label: 'Server' },
  { value: 'other', label: 'Other' },
];

// Define Platform options
const PLATFORM_OPTIONS = [
  { value: 'browser', label: 'Browser' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
  { value: 'roku', label: 'Roku' },
  { value: 'other', label: 'Other' },
];

const EditContextModal = ({ open, onClose, context, accountId }) => {
  // Existing state variables
  const [name, setName] = useState(context.name || '');
  const [type, setType] = useState(context.type || '');
  const [description, setDescription] = useState(context.description || '');
  const [status, setStatus] = useState(context.status || 'active');
  const [platform, setPlatform] = useState(context.platform || '');
  const [url, setUrl] = useState(context.url || '');
  const [app, setApp] = useState(context.app || '');

  // New state variables for sdkURL and pixelURL
  const [sdkURL, setSdkURL] = useState(context.sdkURL || 'https://sdk.tagit.live');
  const [pixelURL, setPixelURL] = useState(context.pixelURL || 'https://pixel.tagit.live');

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // For loading state

  // URL validation regex
  const urlPattern = new RegExp(
    '^(https?:\\/\\/)' + // protocol
      '((([a-zA-Z0-9\\-]+\\.)+[a-zA-Z]{2,})|' + // domain name
      'localhost|' + // localhost
      '\\d{1,3}(\\.\\d{1,3}){3})' + // OR IP (v4) address
      '(\\:\\d+)?(\\/[-a-zA-Z0-9%_.~+]*)*' + // port and path
      '(\\?[;&a-zA-Z0-9%_.~+=-]*)?' + // query string
      '(\\#[-a-zA-Z0-9_]*)?$',
    'i'
  );

  // Clear conditional fields when 'type' changes (if needed)
  useEffect(() => {
    // Since Type is read-only, this useEffect may not be necessary
    // However, keeping it in case Type might change due to future requirements
    if (type !== 'app') {
      setPlatform('');
      setApp('');
    }
    if (type !== 'website') {
      setUrl('');
    }
  }, [type]);

  const handleUpdate = async () => {
    // Basic validations
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }

    // Additional validations based on 'type'
    if (type === 'app') {
      if (!platform) {
        setError('Platform is required for App type.');
        return;
      }
      if (!app.trim()) {
        setError('App ID is required for App type.');
        return;
      }
    }
    if (type === 'website') {
      if (!url.trim()) {
        setError('URL is required for Website type.');
        return;
      }
      // Validate URL format
      if (!urlPattern.test(url)) {
        setError('Please enter a valid URL for the Website.');
        return;
      }
    }

    // Validate sdkURL and pixelURL
    if (!sdkURL.trim()) {
      setError('SDK URL is required.');
      return;
    }
    if (!urlPattern.test(sdkURL)) {
      setError('Please enter a valid SDK URL.');
      return;
    }
    if (!pixelURL.trim()) {
      setError('Pixel URL is required.');
      return;
    }
    if (!urlPattern.test(pixelURL)) {
      setError('Please enter a valid Pixel URL.');
      return;
    }

    try {
      setLoading(true); // Start loading
      setError(null); // Reset any existing errors

      const contextDocRef = doc(db, 'accounts', accountId, 'contexts', context.id);
      const updatedData = {
        // Name and Type are read-only; no need to update them
        description: description.trim() || null,
        status,
        platform: type === 'app' ? platform : null,
        url: type === 'website' ? url.trim() : null,
        app: type === 'app' ? app.trim() : null,
        sdkURL: sdkURL.trim(), // Added sdkURL
        pixelURL: pixelURL.trim(), // Added pixelURL
        lastUpdated: serverTimestamp(),
      };

      await updateDoc(contextDocRef, updatedData);

      // Optionally, display a success message or close the modal
      onClose();
    } catch (error) {
      console.error('Error updating context:', error);
      setError('Failed to update context. Please try again.');
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleClose = () => {
    setError(null);
    setDescription(context.description || '');
    setStatus(context.status || 'active');
    setPlatform(context.platform || '');
    setUrl(context.url || '');
    setApp(context.app || '');
    setSdkURL(context.sdkURL || 'https://sdk.tagit.live'); // Reset sdkURL to default
    setPixelURL(context.pixelURL || 'https://pixel.tagit.live'); // Reset pixelURL to default
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Context</DialogTitle>
      <DialogContent>
        {/* Display Error Message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={3} sx={{ marginTop: 1 }}>
          {/* Name Field - Read-Only */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={name}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              helperText="Name of the context (read-only)"
            />
          </Grid>

          {/* Type Field - Read-Only */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Type"
              value={type}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              helperText="Type of the context (read-only)"
            />
          </Grid>

          {/* New Fields: sdkURL and pixelURL */}
          {/* sdkURL Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="tagIt SDK URL"
              value={sdkURL}
              onChange={(e) => setSdkURL(e.target.value)}
              required
              fullWidth
              helperText="Enter your 1P SDK CNAME URL for sending data to tagIt or use our default."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="The URL endpoint for hitting the tagIt SDK API for sending data">
                      <InfoIcon color="action" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              error={!sdkURL.trim() || !urlPattern.test(sdkURL)}
            />
          </Grid>

          {/* pixelURL Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="tagIt Pixel URL"
              value={pixelURL}
              onChange={(e) => setPixelURL(e.target.value)}
              required
              fullWidth
              helperText="Enter your 1P Pixel CNAME URL for sending data to tagIt or use our default."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="The URL endpoint for hitting the tagIt Pixel API for sending data">
                      <InfoIcon color="action" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              error={!pixelURL.trim() || !urlPattern.test(pixelURL)}
            />
          </Grid>

          {/* Platform Field - Only appears if Type = App */}
          {type === 'app' && (
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                required={type === 'app'}
                fullWidth
                helperText="Select the platform"
              >
                {PLATFORM_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* App ID Field - Only appears if Type = App */}
          {type === 'app' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="App ID"
                value={app}
                onChange={(e) => setApp(e.target.value)}
                required={type === 'app'}
                fullWidth
                helperText="Enter the unique App ID"
              />
            </Grid>
          )}

          {/* URL Field - Only appears if Type = Website */}
          {type === 'website' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required={type === 'website'}
                fullWidth
                helperText="Enter the URL for your website"
                error={!url.trim() || !urlPattern.test(url)}
              />
            </Grid>
          )}

          {/* Description Field */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Enter a description for the context (optional)"
            />
          </Grid>

          {/* Status Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              required
              fullWidth
              helperText="Set the status of the context"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !description.trim() ||
            (type === 'app' && (!platform || !app.trim())) ||
            (type === 'website' && (!url.trim() || !urlPattern.test(url))) ||
            !sdkURL.trim() ||
            !urlPattern.test(sdkURL) ||
            !pixelURL.trim() ||
            !urlPattern.test(pixelURL)
          }
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Define PropTypes for type checking and better maintainability
EditContextModal.propTypes = {
  open: PropTypes.bool.isRequired, // Controls the visibility of the modal
  onClose: PropTypes.func.isRequired, // Function to handle closing the modal
  context: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    type: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    platform: PropTypes.string,
    url: PropTypes.string,
    app: PropTypes.string,
    sdkURL: PropTypes.string, // Added PropTypes for sdkURL
    pixelURL: PropTypes.string, // Added PropTypes for pixelURL
  }).isRequired,
  accountId: PropTypes.string.isRequired, // ID of the parent account
};

export default EditContextModal;
